import clsx from 'clsx';
import React, { useMemo } from 'react';

import { ProButton } from '@components/Button/variants';
import EmailLeadCapture from '@components/EmailLeadCapture';
import OfferPrice from '@components/pages/Upgrade/OfferTable/OfferPrice';
import { useAppContext } from '@contexts/AppContext';
import useEventTracking from '@hooks/useEventTracking';
import useOffers from '@hooks/useOffers';
import usePaywallCTAs from '@hooks/usePaywallCTAs';
import { Theme } from '@interfaces/global';
import { subscribe_path } from '@routes';
import { FormLocation, LeadTypes } from '@utils/enums';
import { SubscriptionStatus } from '@utils/interfaces';

import { GET_STARTED, OFFER_CODES, SUBSCRIBE_NOW, UPGRADE } from './constants';

const PaywallBanner = () => {
  const { trackEvent } = useEventTracking();
  const { initialCurrentUser } = useAppContext();

  const isSubscriber = initialCurrentUser?.is_subscriber;

  const offerCode = useMemo(() => {
    let code = OFFER_CODES[initialCurrentUser?.subscription_status];
    if (isSubscriber) {
      code ||= OFFER_CODES[initialCurrentUser.subscription.plan_type];
    } else {
      code ||= OFFER_CODES[SubscriptionStatus.LEAD];
    }
    return code;
  }, [initialCurrentUser, isSubscriber]);

  const { data: offersData, isLoading: isLoadingOffer } = useOffers({
    offer_codes: [offerCode],
  });

  const offerData = offersData?.[0];

  const { handleCTAClickTracking, paywallCTAPath, paywallCTAText } = usePaywallCTAs({
    currentUser: initialCurrentUser,
    offer: offerCode,
    subscribeText: SUBSCRIBE_NOW,
    upgradeText: UPGRADE,
    trackEvent,
  });
  return (
    <div
      className={clsx(
        'mx-auto mt-24 flex w-full flex-col items-center justify-between bg-grayscale-charcoal px-16 py-24 xs:mt-48 sm:mt-64 sm:flex-row md:px-90',
        initialCurrentUser ? 'sm:gap-24 md:py-48' : 'sm:items-start sm:gap-32 md:py-48'
      )}
      data-testid="paywall"
    >
      <div className="flex flex-col md:max-w-[750px]">
        <h3 className="mx-auto my-0 max-w-[425px] text-start font-suisse-works text-headline-xs text-white sm:max-w-none sm:text-headline-md md:text-headline-lg">
          Deep Research is an exclusive benefit for Pro subscribers.
        </h3>
      </div>
      <div className="mt-32 w-full max-w-full xs:max-w-[425px] sm:mt-0">
        {initialCurrentUser ? (
          !isLoadingOffer && (
            <>
              <div className="flex flex-col items-start justify-start">
                <div className="flex items-center justify-start gap-32">
                  <h3 className="my-0 font-suisse-works text-headline-md text-pro-gold">Pro</h3>
                  <OfferPrice
                    className="[&_span:first]:font-bold [&_span]:!text-grayscale-beige"
                    offer={offerData}
                  />
                </div>
                <p className="my-0 font-suisse-intl text-button-md text-grayscale-beige">
                  Articles + Org Charts + Proprietary Databases
                </p>
              </div>
              <ProButton
                as="link"
                className="mx-0 mt-32 w-[352px]"
                data-testid="paywall-cta"
                href={paywallCTAPath()}
                onClickCapture={handleCTAClickTracking}
              >
                {paywallCTAText}
              </ProButton>
            </>
          )
        ) : (
          <EmailLeadCapture
            buttonClassName="w-full md:w-auto"
            buttonText={paywallCTAText || GET_STARTED}
            formLocation={FormLocation.DeepResearch}
            redirectToOnSuccess={subscribe_path({ offer: offerCode })}
            inputClassName="w-full border-pro-gold"
            signInOptionClass="text-left !opacity-100 *:!opacity-100"
            expandableOathButtonsClassName="!text-grayscale-beige mt-24"
            leadType={LeadTypes.DeepResearchPaywall}
            includeSignInOption
            isPro
            isVertical
            isVerticalMobile
            theme={Theme.Dark}
            withOauth
            withOnlyOauthExpandable
          />
        )}
      </div>
    </div>
  );
};

export default PaywallBanner;
